// ViewModalBoardFieldSection.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Icons
import Icon from 'svg/Icon';

// Components
import CheckboxList from 'components/checkboxlist/CheckboxList';
import ModalField from 'components/modal/field/ModalField';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';

/**
 * ViewModalBoardFieldSection
 *
 * Renders the board grouping field chooser.
 *
 * @returns {ReactNode} The rendered component.
 */
const LAYOUT_OPTIONS = [
    { value: 'kanban', label: 'Kanban' },
    { value: 'lanes', label: 'Lanes' },
];

const ViewModalBoardFieldSection = ({
    isVisible,
    boardField,
    setBoardField,
    boardLayout = 'kanban',
    setBoardLayout,
    enumFields,
    setBoardEnumOptions,
    boardFieldOptions,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [draggingIndex, setDraggingIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    // ----- HANDLERS ----- //

    /**
 * Handles drag start.
 *
 * @param {*} event
 * @param {*} index
 */

    const handleDragStart = (event, index) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));
        setDraggingIndex(index);
        setDragOverIndex(index);
    };

    /**
 * Handles drag over.
 *
 * @param {*} event
 * @param {*} index
 */
    const handleDragOver = (event, index) => {
        event.preventDefault();
        if (dragOverIndex !== index) setDragOverIndex(index);
    };

    /**
 * Handles drop.
 *
 * @param {*} event
 * @param {*} dropIndex
 */
    const handleDrop = (event, dropIndex) => {
        event.preventDefault();

        const fromData = event.dataTransfer.getData('text/plain');
        const parsedIndex = Number.parseInt(fromData, 10);
        const fromIndex = Number.isInteger(parsedIndex) ? parsedIndex : draggingIndex;

        if (!Number.isInteger(fromIndex)) {
            setDraggingIndex(null);
            setDragOverIndex(null);
            return;
        }

        if (fromIndex === dropIndex) {
            setDraggingIndex(null);
            setDragOverIndex(null);
            return;
        }

        const currentIds = Array.isArray(boardFieldOptions)
            ? boardFieldOptions.map((option) => String(option?.id || '').trim()).filter(Boolean)
            : [];

        if (fromIndex < 0 || fromIndex >= currentIds.length) {
            setDraggingIndex(null);
            setDragOverIndex(null);
            return;
        }

        if (dropIndex < 0 || dropIndex >= currentIds.length) {
            setDraggingIndex(null);
            setDragOverIndex(null);
            return;
        }

        const nextIds = [...currentIds];
        const [moved] = nextIds.splice(fromIndex, 1);
        nextIds.splice(dropIndex, 0, moved);

        setBoardEnumOptions((prev) => ({
            fieldKey: boardField,
            optionOrderIds: nextIds,
            sortMode: String(prev?.sortMode || 'inherit').trim().toLowerCase() || 'inherit',
            hiddenOptionIds: [],
        }));

        setDraggingIndex(null);
        setDragOverIndex(null);
    };

    /**
 * Handles drag end.
 */
    const handleDragEnd = () => {
        setDraggingIndex(null);
        setDragOverIndex(null);
    };

    if (!isVisible) return null;

    // ----- RENDER ----- //

    return (

        <ModalField>

            <ModalLabel label="Layout" />
            <ModalHint hint="Kanban shows groups as columns. Lanes stacks the same groups top-to-bottom as tables." />

            <CheckboxList
              value={boardLayout || 'kanban'}
              options={LAYOUT_OPTIONS}
              onChange={(nextValue) => {
                  if (typeof setBoardLayout === 'function') setBoardLayout(nextValue);
              }}
            />

            <ModalLabel label="Group by" />
            <ModalHint hint="Select a Select, Pipeline, or single Relation field to group records on the board." />

            <CheckboxList
              value={boardField}
              options={enumFields
                    .map((field) => {
                        const fieldKey = field?.name || field?.key;
                        if (!fieldKey) return null;
                        const label = field?.label || '';
                        return { value: fieldKey, label };
                    })
                    .filter(Boolean)}
              onChange={(nextValue) => setBoardField(nextValue)}
            />

            {boardField ? (
                <>
                    <ModalLabel label="Group order" />
                    <ModalHint hint="Order groups left-to-right (Kanban) or top-to-bottom (Lanes)." />

                    {Array.isArray(boardFieldOptions) && boardFieldOptions.length ? (
                        <div className="view-modal-property-rows">
                            {boardFieldOptions.map((option, index) => (
                                <div
                                  className={`view-modal-property-item${draggingIndex === index ? ' dragging' : ''}${dragOverIndex === index ? ' drag-over' : ''}`}
                                  style={{
                                        backgroundColor: dragOverIndex === index
                                            ? theme.backgroundMuted
                                            : 'transparent',
                                        borderColor: dragOverIndex === index ? theme.foregroundMuted : 'transparent',
                                    }}
                                  key={option.id}
                                  draggable
                                  onDragEnd={handleDragEnd}
                                  onDragOver={(event) => handleDragOver(event, index)}
                                  onDragStart={(event) => handleDragStart(event, index)}
                                  onDrop={(event) => handleDrop(event, index)}
                                >
                                    <div className="view-modal-property-item-handle">
                                        <Icon
                                          iconKey="drag_indicator"
                                          width={16}
                                          height={16}
                                          color={theme.foregroundMuted}
                                        />
                                    </div>
                                    <div
                                      className="view-modal-property-item-label"
                                      style={{
                                        color: theme.foreground,
                                      }}
                                    >
                                        {option.label || option.value}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div
                          className="view-modal-empty"
                          style={{
                            color: theme.foregroundMuted,
                          }}
                        >
                            No columns available for this field.
                        </div>
                    )}
                </>
            ) : null}

        </ModalField>
    );
};

// Dev Tools
ViewModalBoardFieldSection.displayName = 'ViewModalBoardFieldSection';

export default ViewModalBoardFieldSection;
